import type { RoofSpec } from '@bw/document';
import type { Footprint, Vec2, Vec3 } from './types.js';

/**
 * The roof, derived from the footprint (phase 2 decision).
 *
 * A straight-skeleton roof has a definition that is far easier to compute than
 * the skeleton itself: the height at any point is the distance to the nearest
 * wall, times the pitch. So the roof surface is the LOWER ENVELOPE of one
 * sloping plane per footprint edge, and the face belonging to edge i is simply
 * the part of the footprint where plane i is the lowest.
 *
 * That reduces the whole problem to half-plane clipping — because "plane i is
 * below plane j" is a linear inequality in x and z — and half-plane clipping
 * of a possibly-reflex polygon is something Sutherland-Hodgman already does
 * correctly. No event queue, no skeleton, and hips and valleys fall out of it.
 *
 * A gable is the same construction with the end edges left out of the
 * envelope: the ridge then runs off the ends of the building and the triangles
 * it leaves are the gable walls.
 */

export interface RoofFace {
  /** The footprint edge this slope rises from. */
  readonly edge: number;
  /** The face as a ring in world space, millimetres, Y up. */
  readonly points: readonly Vec3[];
}

export interface RoofModel {
  readonly style: 'hip' | 'gable' | 'none';
  readonly faces: readonly RoofFace[];
  /** The vertical triangles at the ends of a gable roof. */
  readonly gables: readonly (readonly Vec3[])[];
  /** Where two faces meet along the top — ridges and hips. */
  readonly ridges: readonly (readonly [Vec3, Vec3])[];
  readonly eavesY: number;
  readonly apexY: number;
  /**
   * True when the footprint was not something the solver could hip, and it
   * fell back to a gable over the oriented bounding box. The plan view says
   * so rather than quietly drawing the wrong roof.
   */
  readonly fallback: boolean;
}

export const NO_ROOF: RoofModel = {
  style: 'none',
  faces: [],
  gables: [],
  ridges: [],
  eavesY: 0,
  apexY: 0,
  fallback: false,
};

/** Below this the mitre offset of an overhang folds a polygon inside out. */
const MIN_EDGE_MM = 1;

interface EdgePlane {
  /** Inward unit normal of the edge, in plan. */
  readonly nx: number;
  readonly nz: number;
  /** Signed distance from a point to the edge's line: nx*x + nz*z + c. */
  readonly c: number;
  readonly from: Vec2;
  readonly to: Vec2;
  /** False for a gable end, which is left out of the envelope. */
  readonly slopes: boolean;
}

/**
 * Build the roof over a footprint.
 *
 * `eavesY` is the height the roof starts at — the top of the walls. Everything
 * above that is generated; nothing below it is touched.
 */
export function buildRoof(
  footprint: Footprint,
  spec: RoofSpec,
  eavesY: number,
): RoofModel {
  if (spec.style === 'none' || !footprint.closed || footprint.ring.length < 3) return NO_ROOF;

  const fallback = footprint.roofSupport === 'gable-fallback';
  // Normalise the winding before anything reads a normal off it. Every edge
  // formula below assumes counter-clockwise seen from above, and a ring handed
  // in the other way round would put every normal outside the building — which
  // yields a flat roof at eaves height and no error at all.
  const source = counterClockwise(footprint.ring);
  const ring = fallback ? boundingRectangle(source) : offsetRing(source, spec.overhangMm);
  if (ring.length < 3) return NO_ROOF;

  const style = fallback ? 'gable' : spec.style;
  const slope = Math.tan((clampPitch(spec.pitchDeg) * Math.PI) / 180);
  const planes = edgePlanes(ring, style === 'gable');
  const sloping = planes.filter((plane) => plane.slopes);
  if (sloping.length === 0) return NO_ROOF;

  // The envelope construction is only valid where every edge's line is in
  // front of the point. A reflex corner breaks that, so the outline is first
  // split along the bisector at each reflex vertex — which is exactly where
  // the roof's valley runs, so the cut follows a real feature rather than an
  // arbitrary one, and the surface stays continuous across it.
  const faces: RoofFace[] = [];
  for (const piece of convexPieces(ring)) {
    const piecePlanes = edgePlanes(piece.ring, style === 'gable').map((plane, i) => ({
      ...plane,
      // A cut is not a wall: it must take no part in the envelope, or the roof
      // would slope down to an edge that does not exist.
      slopes: plane.slopes && (piece.wall[i] ?? true),
    }));
    for (let i = 0; i < piecePlanes.length; i++) {
      const plane = piecePlanes[i] as EdgePlane;
      if (!plane.slopes) continue;
      const region = clipToLowest(piece.ring, piecePlanes, i);
      if (region.length < 3) continue;
      faces.push({
        edge: i,
        points: region.map((point) => ({
          x: point.x,
          y: eavesY + height(piecePlanes, point) * slope,
          z: point.z,
        })),
      });
    }
  }

  const gables = style === 'gable' ? gableWalls(ring, planes, eavesY, slope) : [];
  let apexY = eavesY;
  for (const face of faces) for (const point of face.points) apexY = Math.max(apexY, point.y);

  return {
    style,
    faces,
    gables,
    ridges: ridgesOf(faces),
    eavesY,
    apexY,
    fallback,
  };
}

/** The pitch a roof is allowed to have. Flat and vertical are both useless. */
function clampPitch(pitchDeg: number): number {
  if (!Number.isFinite(pitchDeg)) return 35;
  return Math.min(75, Math.max(5, pitchDeg));
}

/** Height above the eaves at a point, before the pitch is applied. */
function height(planes: readonly EdgePlane[], point: Vec2): number {
  let lowest = Infinity;
  for (const plane of planes) {
    if (!plane.slopes) continue;
    lowest = Math.min(lowest, plane.nx * point.x + plane.nz * point.z + plane.c);
  }
  return Math.max(0, lowest);
}

/**
 * The part of the footprint where edge `index` is the nearest wall.
 *
 * Clipped by one half-plane per rival edge. Each clip is convex, so a reflex
 * footprint — an L-shaped plan — is handled correctly.
 */
function clipToLowest(
  ring: readonly Vec2[],
  planes: readonly EdgePlane[],
  index: number,
): Vec2[] {
  const mine = planes[index] as EdgePlane;
  let region: Vec2[] = [...ring];
  for (let j = 0; j < planes.length && region.length >= 3; j++) {
    if (j === index) continue;
    const other = planes[j] as EdgePlane;
    if (!other.slopes) continue;
    // Keep where  d_mine <= d_other,  which is linear in x and z.
    const ax = mine.nx - other.nx;
    const az = mine.nz - other.nz;
    const ac = mine.c - other.c;
    if (Math.abs(ax) < 1e-12 && Math.abs(az) < 1e-12) {
      // Parallel and equidistant everywhere: keep the region only if this edge
      // is the lower of the two, otherwise it owns nothing.
      if (ac > 1e-9) return [];
      continue;
    }
    region = clipHalfPlane(region, ax, az, ac);
  }
  return region;
}

/** Keep the part of a ring where ax*x + az*z + ac <= 0. */
function clipHalfPlane(ring: readonly Vec2[], ax: number, az: number, ac: number): Vec2[] {
  const inside = (p: Vec2): number => ax * p.x + az * p.z + ac;
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) {
    const current = ring[i] as Vec2;
    const previous = ring[(i + ring.length - 1) % ring.length] as Vec2;
    const dc = inside(current);
    const dp = inside(previous);
    if (dc <= 1e-9) {
      if (dp > 1e-9) out.push(cross(previous, current, dp, dc));
      out.push(current);
    } else if (dp <= 1e-9) {
      out.push(cross(previous, current, dp, dc));
    }
  }
  return dedupe(out);
}

function cross(a: Vec2, b: Vec2, da: number, db: number): Vec2 {
  const t = da / (da - db);
  return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
}

function dedupe(ring: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const point of ring) {
    const last = out[out.length - 1];
    if (last !== undefined && Math.hypot(last.x - point.x, last.z - point.z) < 1e-7) continue;
    out.push(point);
  }
  const first = out[0];
  const last = out[out.length - 1];
  if (out.length > 1 && first !== undefined && last !== undefined) {
    if (Math.hypot(first.x - last.x, first.z - last.z) < 1e-7) out.pop();
  }
  return out;
}

/**
 * One plane per edge.
 *
 * For a gable, the edges most nearly across the ridge are marked as not
 * sloping: they take no part in the envelope, so the roof runs straight off
 * the end of the building and leaves a triangle to be walled in.
 */
function edgePlanes(ring: readonly Vec2[], gable: boolean): EdgePlane[] {
  const ridge = gable ? ridgeDirection(ring) : null;
  const planes: EdgePlane[] = [];
  for (let i = 0; i < ring.length; i++) {
    const from = ring[i] as Vec2;
    const to = ring[(i + 1) % ring.length] as Vec2;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz);
    if (length < MIN_EDGE_MM) continue;
    // Counter-clockwise seen from above — with +z running down the page, that
    // is a NEGATIVE shoelace area — makes the inward normal of an edge running
    // (dx, dz) equal to (dz, -dx). Checked against a rectangle's top edge,
    // which runs +x and must point toward -z.
    const nx = dz / length;
    const nz = -dx / length;
    const slopes =
      ridge === null ||
      // Along the ridge: this edge is an eaves edge and slopes.
      Math.abs((dx / length) * ridge.x + (dz / length) * ridge.z) > Math.SQRT1_2;
    planes.push({ nx, nz, c: -(nx * from.x + nz * from.z), from, to, slopes });
  }
  return planes;
}

/**
 * Which way this roof's ridge will run, or null if it will not have one.
 *
 * The same decisions `buildRoof` makes, made where a caller can see them:
 * whether a roof is built at all, whether the hip solver has to fall back to a
 * gable, and which ring the ridge is then measured on. It is separate because
 * the ridge decides which walls are EAVES walls and which are gable ends, and
 * that has to be known before the roof is built — the eaves height depends on
 * it.
 */
export function ridgeDirectionFor(footprint: Footprint, spec: RoofSpec): Vec2 | null {
  if (spec.style === 'none' || !footprint.closed || footprint.ring.length < 3) return null;
  const fallback = footprint.roofSupport === 'gable-fallback';
  if (!fallback && spec.style !== 'gable') return null;
  const source = counterClockwise(footprint.ring);
  const ring = fallback ? boundingRectangle(source) : offsetRing(source, spec.overhangMm);
  return ring.length < 3 ? null : ridgeDirection(ring);
}

/** The direction a gable ridge runs: along the longest edge of the outline. */
function ridgeDirection(ring: readonly Vec2[]): Vec2 {
  let best = { x: 1, z: 0 };
  let longest = 0;
  for (let i = 0; i < ring.length; i++) {
    const from = ring[i] as Vec2;
    const to = ring[(i + 1) % ring.length] as Vec2;
    const length = Math.hypot(to.x - from.x, to.z - from.z);
    if (length > longest) {
      longest = length;
      best = { x: (to.x - from.x) / length, z: (to.z - from.z) / length };
    }
  }
  return best;
}

/** The vertical triangle each gable end leaves to be filled in. */
function gableWalls(
  ring: readonly Vec2[],
  planes: readonly EdgePlane[],
  eavesY: number,
  slope: number,
): (readonly Vec3[])[] {
  const out: (readonly Vec3[])[] = [];
  for (const plane of planes) {
    if (plane.slopes) continue;
    const a = plane.from;
    const b = plane.to;
    // Walk the whole end from one eaves corner to the other, sampling the roof
    // line above it. The corners are part of the same run, so the collinearity
    // test can drop everything between them that does not actually turn — for
    // a plain gable that leaves the two corners and one apex.
    const steps = 48;
    const profile: Vec3[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const point = { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
      profile.push({ x: point.x, y: eavesY + height(planes, point) * slope, z: point.z });
    }
    const wall = simplifyProfile(profile);
    if (wall.length >= 3) out.push(wall);
  }
  void ring;
  return out;
}

/**
 * Drop sampled points that sit on a straight run.
 *
 * The profile is sampled rather than solved, so a plain gable arrives as
 * dozens of collinear points; only the ones where the roof line actually turns
 * are corners of the end wall.
 */
function simplifyProfile(points: readonly Vec3[]): Vec3[] {
  if (points.length <= 2) return [...points];
  const out: Vec3[] = [];
  for (let i = 0; i < points.length; i++) {
    const previous = points[i - 1];
    const at = points[i] as Vec3;
    const next = points[i + 1];
    if (previous === undefined || next === undefined) {
      out.push(at);
      continue;
    }
    if (Math.abs(at.y - (previous.y + next.y) / 2) > 0.5) out.push(at);
  }
  return out;
}

/** Where two roof faces share an edge — the ridges and hips. */
function ridgesOf(faces: readonly RoofFace[]): (readonly [Vec3, Vec3])[] {
  const out: (readonly [Vec3, Vec3])[] = [];
  const seen = new Set<string>();
  const key = (a: Vec3, b: Vec3): string => {
    const one = `${a.x.toFixed(2)},${a.y.toFixed(2)},${a.z.toFixed(2)}`;
    const two = `${b.x.toFixed(2)},${b.y.toFixed(2)},${b.z.toFixed(2)}`;
    return one < two ? `${one}|${two}` : `${two}|${one}`;
  };
  for (const face of faces) {
    for (let i = 0; i < face.points.length; i++) {
      const a = face.points[i] as Vec3;
      const b = face.points[(i + 1) % face.points.length] as Vec3;
      // Only edges that are off the ground can be a ridge or a hip.
      if (a.y <= 1e-6 && b.y <= 1e-6) continue;
      const id = key(a, b);
      if (seen.has(id)) {
        out.push([a, b]);
        continue;
      }
      seen.add(id);
    }
  }
  return out;
}

/**
 * Push every edge outward by the overhang, mitring at the corners.
 *
 * Offsetting a polygon properly needs a full straight-skeleton treatment for
 * large distances; an eaves overhang is small next to a building, so mitring
 * consecutive offset lines is both correct here and far simpler. A degenerate
 * result — which only a huge overhang on a tiny plan can produce — falls back
 * to the un-offset ring rather than an inside-out one.
 */
export function offsetRing(ring: readonly Vec2[], distance: number): Vec2[] {
  if (distance <= 0) return [...ring];
  const lines: { nx: number; nz: number; c: number }[] = [];
  for (let i = 0; i < ring.length; i++) {
    const from = ring[i] as Vec2;
    const to = ring[(i + 1) % ring.length] as Vec2;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz);
    if (length < MIN_EDGE_MM) return [...ring];
    const nx = dz / length;
    const nz = -dx / length;
    // Inward normal is (nx, nz); pushing out means allowing a larger distance.
    lines.push({ nx, nz, c: -(nx * from.x + nz * from.z) + distance });
  }

  const out: Vec2[] = [];
  for (let i = 0; i < lines.length; i++) {
    const a = lines[(i + lines.length - 1) % lines.length] as { nx: number; nz: number; c: number };
    const b = lines[i] as { nx: number; nz: number; c: number };
    const determinant = a.nx * b.nz - a.nz * b.nx;
    if (Math.abs(determinant) < 1e-9) return [...ring];
    out.push({
      x: (-a.c * b.nz + b.c * a.nz) / determinant,
      z: (-a.nx * b.c + b.nx * a.c) / determinant,
    });
  }
  // A mitre that folded the outline inside out is worse than no overhang.
  return signedArea(out) * signedArea(ring) > 0 ? out : [...ring];
}

interface Piece {
  readonly ring: readonly Vec2[];
  /** Per edge: true for a real wall, false for a cut introduced by splitting. */
  readonly wall: readonly boolean[];
}

/**
 * Split an outline at its reflex corners until every piece is convex.
 *
 * The cut runs along the interior bisector of the reflex vertex, which is the
 * line equidistant from its two walls — the valley of the roof. Cutting there
 * means the two halves agree along the seam instead of meeting at a step.
 */
export function convexPieces(ring: readonly Vec2[]): Piece[] {
  return splitPiece({ ring, wall: ring.map(() => true) }, 0);
}

/**
 * Recurses on whole pieces rather than bare rings.
 *
 * An earlier version passed only the ring down and rebuilt the wall flags
 * afterwards by matching each edge back onto its parent. That is exactly the
 * kind of derived bookkeeping that goes quietly wrong: one cut came back
 * marked as a wall, and the roof grew a seventh plane sloping down to an edge
 * that does not exist.
 */
function splitPiece(piece: Piece, depth: number): Piece[] {
  if (depth > 12) return [piece];
  const reflex = firstReflex(piece.ring);
  if (reflex < 0) return [piece];

  const cut = bisectorCut(piece.ring, reflex);
  if (cut === null) return [piece];

  const [a, b] = splitAt(piece, reflex, cut.edge, cut.at);
  if (a.ring.length < 3 || b.ring.length < 3) return [piece];
  if (a.wall.length !== a.ring.length || b.wall.length !== b.ring.length) return [piece];
  return [...splitPiece(a, depth + 1), ...splitPiece(b, depth + 1)];
}


/** Index of the first corner that turns the wrong way, or -1. */
function firstReflex(ring: readonly Vec2[]): number {
  for (let i = 0; i < ring.length; i++) {
    const previous = ring[(i + ring.length - 1) % ring.length] as Vec2;
    const at = ring[i] as Vec2;
    const next = ring[(i + 1) % ring.length] as Vec2;
    const cross =
      (at.x - previous.x) * (next.z - at.z) - (at.z - previous.z) * (next.x - at.x);
    // Counter-clockwise from above is negative area, so a convex turn is
    // negative too; a positive cross is a reflex corner.
    if (cross > 1e-6) return i;
  }
  return -1;
}

/** Where the interior bisector from a reflex corner meets the outline. */
function bisectorCut(
  ring: readonly Vec2[],
  index: number,
): { edge: number; at: Vec2 } | null {
  const previous = ring[(index + ring.length - 1) % ring.length] as Vec2;
  const at = ring[index] as Vec2;
  const next = ring[(index + 1) % ring.length] as Vec2;
  const toPrevious = unit(previous.x - at.x, previous.z - at.z);
  const toNext = unit(next.x - at.x, next.z - at.z);
  if (toPrevious === null || toNext === null) return null;
  // The sum of the two edge directions bisects the OUTSIDE of a reflex corner,
  // so the interior bisector is its opposite.
  let dx = -(toPrevious.x + toNext.x);
  let dz = -(toPrevious.z + toNext.z);
  if (Math.hypot(dx, dz) < 1e-9) {
    // A straight-through reflex corner: cut perpendicular to the walls.
    dx = -toNext.z;
    dz = toNext.x;
  }
  const direction = unit(dx, dz);
  if (direction === null) return null;

  let best: { edge: number; at: Vec2; distance: number } | null = null;
  for (let i = 0; i < ring.length; i++) {
    if (i === index || (i + 1) % ring.length === index) continue;
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    const hit = rayHitsSegment(at, direction, a, b);
    if (hit === null) continue;
    if (best === null || hit.distance < best.distance) {
      best = { edge: i, at: hit.point, distance: hit.distance };
    }
  }
  return best === null ? null : { edge: best.edge, at: best.at };
}

function unit(x: number, z: number): Vec2 | null {
  const length = Math.hypot(x, z);
  return length < 1e-9 ? null : { x: x / length, z: z / length };
}

function rayHitsSegment(
  from: Vec2,
  direction: Vec2,
  a: Vec2,
  b: Vec2,
): { point: Vec2; distance: number } | null {
  const ex = b.x - a.x;
  const ez = b.z - a.z;
  const denominator = direction.x * ez - direction.z * ex;
  if (Math.abs(denominator) < 1e-12) return null;
  // from + t*direction = a + u*edge. Crossing with the edge gives t, crossing
  // with the direction gives u; both divide by the SAME determinant. Dividing
  // u by its negation silently rejects every hit whose determinant is
  // positive, which leaves a reflex corner unsplit and the roof short of two
  // slopes — with no error anywhere.
  const t = ((a.x - from.x) * ez - (a.z - from.z) * ex) / denominator;
  const u = ((a.x - from.x) * direction.z - (a.z - from.z) * direction.x) / denominator;
  if (t <= 1e-6 || u < -1e-9 || u > 1 + 1e-9) return null;
  return { point: { x: from.x + direction.x * t, z: from.z + direction.z * t }, distance: t };
}

/**
 * Cut the ring from vertex `index` to a point on edge `edge`.
 *
 * Vertices and edge flags are built as pairs and deduplicated together, so a
 * cut that lands exactly on an existing corner — which happens whenever a
 * reflex bisector runs corner to corner — cannot slide the flags out of step
 * with the points.
 */
function splitAt(piece: Piece, index: number, edge: number, at: Vec2): [Piece, Piece] {
  const ring = piece.ring;
  const n = ring.length;
  const flag = (i: number): boolean => piece.wall[((i % n) + n) % n] ?? true;
  const vertex = (i: number): Vec2 => ring[((i % n) + n) % n] as Vec2;

  // Each entry is a corner plus the flag of the edge LEAVING it.
  const first: { point: Vec2; wall: boolean }[] = [];
  for (let i = index; ; i++) {
    const at_i = ((i % n) + n) % n;
    first.push({ point: vertex(at_i), wall: flag(at_i) });
    if (at_i === edge) break;
    if (i - index > n) break;
  }
  first.push({ point: at, wall: false }); // the cut, back to `index`

  const second: { point: Vec2; wall: boolean }[] = [{ point: at, wall: flag(edge) }];
  for (let i = edge + 1; ; i++) {
    const at_i = ((i % n) + n) % n;
    second.push({ point: vertex(at_i), wall: flag(at_i) });
    if (at_i === index) break;
    if (i - edge > n) break;
  }
  // The last entry is vertex `index`, whose outgoing edge is the cut.
  const last = second[second.length - 1];
  if (last !== undefined) second[second.length - 1] = { point: last.point, wall: false };

  return [tidy(first), tidy(second)];
}

/**
 * Drop repeated corners, keeping the flag of the edge that survives.
 *
 * When two corners coincide the edge between them has no length; the flag that
 * matters is the one leaving the pair.
 */
function tidy(entries: readonly { point: Vec2; wall: boolean }[]): Piece {
  const kept: { point: Vec2; wall: boolean }[] = [];
  for (const entry of entries) {
    const previous = kept[kept.length - 1];
    if (previous !== undefined && same(previous.point, entry.point)) {
      kept[kept.length - 1] = { point: previous.point, wall: entry.wall };
      continue;
    }
    kept.push(entry);
  }
  const head = kept[0];
  const tail = kept[kept.length - 1];
  if (kept.length > 1 && head !== undefined && tail !== undefined && same(head.point, tail.point)) {
    kept.pop();
  }
  return { ring: kept.map((entry) => entry.point), wall: kept.map((entry) => entry.wall) };
}

function same(a: Vec2, b: Vec2): boolean {
  return Math.hypot(a.x - b.x, a.z - b.z) < 1e-7;
}

/** Counter-clockwise seen from above, whichever way the ring arrived. */
function counterClockwise(ring: readonly Vec2[]): Vec2[] {
  return signedArea(ring) > 0 ? [...ring].reverse() : [...ring];
}

function signedArea(ring: readonly Vec2[]): number {
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    total += a.x * b.z - b.x * a.z;
  }
  return total / 2;
}

/** The axis-aligned box a fallback gable is built over. */
function boundingRectangle(ring: readonly Vec2[]): Vec2[] {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const point of ring) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z);
    maxZ = Math.max(maxZ, point.z);
  }
  // Counter-clockwise seen from above, matching every other ring here.
  return [
    { x: minX, z: minZ },
    { x: minX, z: maxZ },
    { x: maxX, z: maxZ },
    { x: maxX, z: minZ },
  ];
}

/** Exported for the tests: does this face lie inside the footprint? */
export function faceArea(points: readonly Vec3[]): number {
  return Math.abs(signedArea(points.map((point) => ({ x: point.x, z: point.z }))));
}
